import { type CreditUsageStats } from '~/components/CreditUsageMeter/types';
import { getCreditUsageState, getPercentageUsed } from '~/components/CreditUsageMeter/utils';
import { type CreditUsageUpsellAlertProps } from '~/components/CreditUsageUpsellAlert';
import { type EnhancedSubscription } from '~/providers/SubscriptionProvider';

export const getCreditUsageUpsellAlertCandidate = ({
  creditStats,
  subscription,
  canEditPlan,
  showPlansModal,
  showDemoFormModal,
}: {
  creditStats: CreditUsageStats;
  subscription: EnhancedSubscription;
  canEditPlan: boolean;
  showPlansModal: () => void;
  showDemoFormModal: () => void;
}) => {
  if (!creditStats.allocated) {
    return null;
  }

  let candidate: ({ useSessionStorage?: boolean } & Omit<CreditUsageUpsellAlertProps, 'onDismiss'>) | null = null;

  const usageState = getCreditUsageState({ allocated: creditStats.allocated, used: creditStats.used });
  const percentageUsed = Math.round(getPercentageUsed(creditStats.used.total, creditStats.allocated));
  const showContactAir = subscription.isEnterprise || subscription.isOnBusinessPlan;

  switch (usageState) {
    case 'depleted': {
      let description =
        'New generations are paused. You can still save assets already in your canvas to your workspace.';

      if (!subscription.isCreditModel) {
        description = showContactAir
          ? 'You’ve used all of your credits this month. Talk to your account team to add more or explore new plans.'
          : 'You’ve used all of your credits this month. Upgrade now to keep your workflow going.';
      }
      candidate = {
        useSessionStorage: true,
        title: subscription.isCreditModel ? 'No credits remaining' : 'You’ve reached your credit limit',
        description,
        ctaButton: {
          text: 'Upgrade to continue',
          onClick: showPlansModal,
        },
        dismissButton: {
          text: canEditPlan ? 'Remind me later' : 'Dismiss',
        },
      };
      break;
    }
    case '90%': {
      let description = `You’ve used ${percentageUsed}% of your credits this month.`;

      if (subscription.isCreditModel) {
        description += ` Uploads and AI generation will pause when you hit 0.`;
      }

      if (!canEditPlan) {
        description += ` Talk to your admin to upgrade your plan.`;
      } else if (showContactAir) {
        description += ` Talk to your account team to add more or explore new plans.`;
      } else {
        description += ` Upgrade now to keep your workflow going.`;
      }
      candidate = {
        title: 'You’re approaching your credit limit',
        description,
        ctaButton: {
          text: 'Upgrade plan',
          onClick: showPlansModal,
        },
      };
      break;
    }
    case '75%': {
      let description = `You’ve used ${percentageUsed}% of your credits this month.`;

      if (!canEditPlan) {
        description += ` Talk to your admin to add more or upgrade.`;
      } else if (showContactAir) {
        description += ` Talk to your account team to add more or explore new plans.`;
      } else {
        description += ` Upgrade your plan to get more credits and keep things moving.`;
      }
      candidate = {
        title: 'You’re using credits quickly',
        description,
        ctaButton: {
          text: 'Upgrade plan',
          onClick: showPlansModal,
        },
      };
      break;
    }
    case '50%': {
      let description = `You’ve used ${percentageUsed}% of your credits this month.`;

      if (!canEditPlan) {
        description += ` Keep going or talk to your admin to get more.`;
      } else if (showContactAir) {
        description += ` Keep going or talk to your account team to get more.`;
      } else {
        description += ` Keep going or explore plans to get more.`;
      }
      candidate = {
        title: 'You’re halfway through your credits',
        description,
        ctaButton: {
          text: 'Upgrade plan',
          onClick: showPlansModal,
        },
      };
      break;
    }
    default:
      return null;
  }

  if (showContactAir) {
    candidate.ctaButton = {
      text: 'Contact Air',
      onClick: showDemoFormModal,
    };
  }

  if (!canEditPlan) {
    candidate.ctaButton = undefined;
  }

  return {
    ...candidate,
    creditUsageState: usageState,
  };
};
